import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { AuthProvider } from './hooks/useAuth';
import Layout from './components/Layout';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import LoadingSpinner from './components/LoadingSpinner';
import { OfflineBanner } from './components/OfflineComponents';
import ErrorBoundary from './components/common/ErrorBoundary';
import { applyVerticalTheme, normalizeVertical, type VerticalType } from './config/vertical';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Blocks = lazy(() => import('./pages/Blocks'));
const BlockDashboard = lazy(() => import('./pages/BlockDashboard'));
const DiseasePressureDashboard = lazy(() => import('./pages/DiseasePressureDashboard'));
const Inputs = lazy(() => import('./pages/Inputs'));
const Harvests = lazy(() => import('./pages/Harvests'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Scouting = lazy(() => import('./pages/Scouting'));
const CompletedQualityGates = lazy(() => import('./pages/CompletedQualityGates'));
const WineTastingPage = lazy(() => import('./pages/WineTasting'));
const Compliance = lazy(() => import('./pages/Compliance'));
const Permits = lazy(() => import('./pages/Permits'));
const ReferenceData = lazy(() => import('./pages/ReferenceData'));
const SensorConfiguration = lazy(() => import('./pages/SensorConfiguration'));
const SensorData = lazy(() => import('./pages/SensorData'));
const Contracts = lazy(() => import('./pages/Contracts'));
const WorkerDashboard = lazy(() => import('./pages/WorkerDashboard'));
const WorkerReporting = lazy(() => import('./pages/WorkerReporting'));
const ManagerDashboard = lazy(() => import('./pages/ManagerDashboard'));
const TimeExportManagement = lazy(() => import('./pages/TimeExportManagement'));
const VineyardInfo = lazy(() => import('./pages/VineyardInfo'));
const TestAnalytics = lazy(() => import('./pages/TestAnalytics'));
const Dictionary = lazy(() => import('./pages/Dictionary'));
const Calculations = lazy(() => import('./pages/Calculations'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const AdminBlog = lazy(() => import('./pages/AdminBlog'));
const Resources = lazy(() => import('./pages/Resources'));
const ResourcePreview = lazy(() => import('./pages/ResourcePreview'));
const Subscription = lazy(() => import('./pages/Subscription'));
const UserWorkerManagement = lazy(() => import('./pages/UserWorkerManagement'));
const EquipmentManagement = lazy(() => import('./pages/EquipmentManagement'));
const Inventory = lazy(() => import('./pages/Inventory'));
const EquipmentScheduleLive = lazy(() => import('./pages/EquipmentScheduleLive'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const ManagerTasks = lazy(() => import('./pages/ManagerTasks'));
const JoinVineyard = lazy(() => import('./pages/JoinVineyard'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AcceptInvitation = lazy(() => import('./pages/AcceptInvitation'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const EnhancedAIChat = lazy(() => import('./components/ai/EnhancedAIChat'));
const AIMonitoringConfig = lazy(() => import('./components/ai/AIMonitoringConfig'));
const MonitoringDashboard = lazy(() => import('./components/ai/MonitoringDashboard'));
const GrapiaOperator = lazy(() => import('./pages/GrapiaOperator'));

const PUBLIC_VERTICALS: VerticalType[] = ['vineyard', 'orchard', 'greenhouse'];

type PublicRouteConfig = {
  path: string;
  element: (vertical: VerticalType) => React.ReactElement;
  legacyPaths?: string[];
};

const PUBLIC_ROUTE_CONFIGS: PublicRouteConfig[] = [
  { path: '', element: (vertical) => <Landing vertical={vertical} />, legacyPaths: ['/'] },
  { path: 'login', element: (vertical) => <Login vertical={vertical} />, legacyPaths: ['/login'] },
  { path: 'register', element: (vertical) => <Register vertical={vertical} />, legacyPaths: ['/register'] },
  { path: 'signup', element: (vertical) => <Register vertical={vertical} />, legacyPaths: ['/signup'] },
  { path: 'join-vineyard', element: (vertical) => <JoinVineyard vertical={vertical} />, legacyPaths: ['/join-vineyard'] },
  { path: 'reset-password', element: (vertical) => <ResetPassword vertical={vertical} />, legacyPaths: ['/reset-password'] },
  { path: 'accept-invitation', element: (vertical) => <AcceptInvitation vertical={vertical} />, legacyPaths: ['/accept-invitation'] },
  { path: 'accept-invitation/:token', element: (vertical) => <AcceptInvitation vertical={vertical} />, legacyPaths: ['/accept-invitation/:token'] },
  { path: 'terms', element: (vertical) => <TermsAndConditions vertical={vertical} />, legacyPaths: ['/terms'] },
  { path: 'privacy', element: (vertical) => <PrivacyPolicy vertical={vertical} />, legacyPaths: ['/privacy'] },
  { path: 'about', element: (vertical) => <AboutUs vertical={vertical} />, legacyPaths: ['/about'] },
  { path: 'blog', element: (vertical) => <Blog vertical={vertical} />, legacyPaths: ['/blog'] },
  { path: 'blog/:slug', element: (vertical) => <BlogPost vertical={vertical} />, legacyPaths: ['/blog/:slug'] },
  { path: 'resources', element: (vertical) => <Resources vertical={vertical} />, legacyPaths: ['/resources'] },
  { path: 'resources/preview/:resourceName', element: (vertical) => <ResourcePreview vertical={vertical} />, legacyPaths: ['/resources/preview/:resourceName'] },
];

function renderPublicRoutes(): React.ReactElement[] {
  const routes: React.ReactElement[] = [];

  for (const config of PUBLIC_ROUTE_CONFIGS) {
    for (const vertical of PUBLIC_VERTICALS) {
      const path = config.path ? `/${vertical}/${config.path}` : `/${vertical}`;
      routes.push(
        <Route
          key={`${vertical}-${config.path || 'root'}`}
          path={path}
          element={config.element(vertical)}
        />
      );
    }

    if (config.legacyPaths) {
      for (const legacyPath of config.legacyPaths) {
        routes.push(
          <Route
            key={`legacy-${legacyPath}`}
            path={legacyPath}
            element={config.element('vineyard')}
          />
        );
      }
    }
  }

  return routes;
}

function AppContent() {
  const { user, loading } = useAuth();
  const [showEnhancedAI, setShowEnhancedAI] = React.useState(false);
  const [operatorUnreadCount, setOperatorUnreadCount] = React.useState(0);
  const lastOperatorUnreadRef = React.useRef(0);
  const location = useLocation();

  const routeVertical = React.useMemo<VerticalType>(() => {
    const firstSegment = location.pathname.split('/').filter(Boolean)[0];
    return normalizeVertical(firstSegment);
  }, [location.pathname]);

  const activeVertical = (user?.organization_type ? normalizeVertical(user.organization_type) : routeVertical);

  useEffect(() => {
    applyVerticalTheme(activeVertical);
  }, [activeVertical]);

  useEffect(() => {
    if (!user?.vineyard_id || user?.role === 'worker') {
      setOperatorUnreadCount(0);
      lastOperatorUnreadRef.current = 0;
      return;
    }

    let cancelled = false;
    const loadOperatorMessages = async () => {
      try {
        const response = await import('./utils/api').then(({ api }) =>
          api.get('/api/ai/operator/dashboard-messages', {
            params: { vineyard_id: user.vineyard_id },
          })
        );
        if (cancelled) return;
        const unread = Number(response.data?.unread_count || 0);
        const shouldAutoOpen = !!response.data?.auto_open;
        setOperatorUnreadCount(unread);
        if (!showEnhancedAI && shouldAutoOpen && unread > lastOperatorUnreadRef.current) {
          setShowEnhancedAI(true);
        }
        lastOperatorUnreadRef.current = unread;
      } catch {
        if (!cancelled) {
          setOperatorUnreadCount(0);
        }
      }
    };

    loadOperatorMessages();
    const intervalId = window.setInterval(loadOperatorMessages, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [showEnhancedAI, user?.role, user?.vineyard_id]);

  // Disable custom service worker in production to avoid stale cache routing issues.
  // We can reintroduce this after implementing a hashed-asset-safe offline strategy.
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations()
        .then((registrations) => {
          registrations.forEach((registration) => {
            registration.unregister();
          });
        })
        .catch((error) => {
          console.log('Service Worker cleanup failed:', error);
        });
    }
  }, []);

  const routeLoadingFallback = (
    <div className="min-h-screen flex items-center justify-center bg-vineyard-light">
      <LoadingSpinner size="xl" className="text-grape-600" vertical={activeVertical} />
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-vineyard-light">
        <LoadingSpinner size="xl" className="text-grape-600" vertical={activeVertical} />
      </div>
    );
  }

  if (!user) {
    return (
      <Suspense fallback={routeLoadingFallback}>
        <Routes>
          {renderPublicRoutes()}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  // Check user role - if worker, show worker dashboard
  if (user?.role === 'worker') {
    // Worker dashboard - simplified interface for workers only
    return (
      <Suspense fallback={routeLoadingFallback}>
        <Routes>
          <Route path="/" element={<Navigate to="/worker-dashboard" replace />} />
          <Route path="/dashboard" element={<Navigate to="/worker-dashboard" replace />} />
          <Route path="/worker-dashboard" element={<WorkerDashboard />} />
          <Route path="/worker-reporting" element={<WorkerReporting />} />
          <Route path="*" element={<Navigate to="/worker-dashboard" replace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Layout>
      <OfflineBanner />
      <Suspense fallback={routeLoadingFallback}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/manager-dashboard" element={<ManagerDashboard />} />
          <Route path="/time-export" element={<TimeExportManagement />} />
          <Route path="/blocks" element={<Blocks />} />
                    <Route path="/blocks/:id" element={
                      <ErrorBoundary>
                        <BlockDashboard />
                      </ErrorBoundary>
                    } />
                    <Route path="/block-dashboard" element={
                      <ErrorBoundary>
                        <BlockDashboard />
                      </ErrorBoundary>
                    } />
          <Route path="/disease-pressure" element={<DiseasePressureDashboard />} />
          <Route path="/inputs" element={<Inputs />} />
          <Route path="/harvests" element={<Harvests />} />
          <Route path="/scouting" element={<Scouting />} />
          <Route path="/completed-checklists" element={<Navigate to="/completed-quality-gates" replace />} />
          <Route path="/completed-quality-gates" element={<CompletedQualityGates />} />
          <Route path="/maintenance" element={<Maintenance />} />
          <Route path="/wine-tasting" element={<WineTastingPage />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/permits" element={<Permits />} />
          <Route path="/reference-data" element={<ReferenceData />} />
          <Route path="/sensors" element={<SensorData />} />
          <Route path="/sensors/config" element={<SensorConfiguration />} />
          <Route path="/contracts" element={<Contracts />} />
          <Route path="/equipment" element={<EquipmentManagement />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/machinery" element={<EquipmentScheduleLive />} />
          <Route path="/user-management" element={<UserWorkerManagement />} />
          <Route path="/manager-tasks" element={<ManagerTasks />} />
          <Route path="/vineyard" element={<VineyardInfo />} />
          {/* <Route path="/ai-predictions" element={<AIPredictions />} /> */}
          <Route path="/test-analytics" element={<TestAnalytics />} />
          <Route path="/dictionary" element={<Dictionary />} />
          <Route path="/calculations" element={<Calculations />} />
          <Route path="/blog" element={<Blog vertical={activeVertical} />} />
          <Route path="/blog/:slug" element={<BlogPost vertical={activeVertical} />} />
          <Route path="/admin/blog" element={<AdminBlog />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/preview/:resourceName" element={<ResourcePreview />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/ai/monitoring" element={<AIMonitoringConfig />} />
          <Route path="/ai/insights" element={<MonitoringDashboard />} />
          <Route path="/ai/operator" element={<GrapiaOperator />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <EnhancedAIChat 
          isOpen={showEnhancedAI} 
          onToggle={() => setShowEnhancedAI(!showEnhancedAI)}
          initialSessionType="general"
          availableTools={[]}
          vertical={activeVertical}
          operatorUnreadCount={operatorUnreadCount}
        />
      </Suspense>
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App; 
